import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, Send, Users, History, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function NotificationsTab() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !body.trim()) {
      toast.error('Título y mensaje son requeridos');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/notifications/send`,
        { title, body, url },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(`Notificación enviada a ${res.data.sent} suscriptores`);
        setTitle('');
        setBody('');
        setUrl('/');
        fetchStats(); // Refresh stats
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Error al enviar notificación');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#FFE500]/20 rounded-lg">
              <Users className="w-5 h-5 text-[#FFE500]" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Suscriptores activos</p>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : stats?.total_subscribers || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Bell className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Inactivos/Expirados</p>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : stats?.inactive_subscribers || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <History className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Notificaciones enviadas</p>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : stats?.recent_notifications?.length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Send Notification Form */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Send className="w-5 h-5 text-[#FFE500]" />
          Enviar Notificación Push
        </h3>

        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Título</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="¡Nuevo episodio disponible!"
              className="bg-gray-900 border-gray-600"
              maxLength={50}
            />
            <p className="text-xs text-gray-500 mt-1">{title.length}/50 caracteres</p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Mensaje</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Ya está disponible el episodio 25 de Vete a la Versh..."
              className="bg-gray-900 border-gray-600"
              rows={3}
              maxLength={150}
            />
            <p className="text-xs text-gray-500 mt-1">{body.length}/150 caracteres</p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">URL de destino</label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/"
              className="bg-gray-900 border-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">Ruta donde irá el usuario al tocar la notificación</p>
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-gray-400">
              Se enviará a <span className="text-[#FFE500] font-bold">{stats?.total_subscribers || 0}</span> suscriptores
            </p>
            <Button
              type="submit"
              disabled={sending || !title.trim() || !body.trim()}
              className="bg-[#FFE500] hover:bg-[#FFE500]/80 text-black"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Notificación
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* History */}
      {stats?.recent_notifications?.length > 0 && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-[#FFE500]" />
            Historial Reciente
          </h3>

          <div className="space-y-3">
            {stats.recent_notifications.map((notif, index) => (
              <div 
                key={notif.id || index}
                className="bg-gray-900 rounded-lg p-4 border border-gray-700"
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-white">{notif.title}</h4>
                  <span className="text-xs text-gray-500">{formatDate(notif.sent_at)}</span>
                </div>
                <p className="text-gray-400 text-sm mb-2">{notif.body}</p>
                <div className="flex gap-4 text-xs">
                  <span className="text-green-400">✓ {notif.success_count} enviados</span>
                  {notif.failure_count > 0 && (
                    <span className="text-red-400">✗ {notif.failure_count} fallidos</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
